import { Box, Button, Divider, FormControlLabel, Grid, Switch, TextField, IconButton } from "@mui/material";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import React, { useState } from "react";
import { useLocation } from 'react-router-dom';
import { useAuth, useDOUploadFile } from "hooks";
import LoadingWrapper from "components/Shared/LoadingWrapper";
import InnerShadowBox from "components/Shared/InnerShadowBox";
import { BaseTextField } from "components/FormBuilder/fields/BaseTextField";
import FileDropzone from "components/Shared/FileDropzone";
import { useListListQuery } from "store/services/listService";
import { ENTITY_LIST_CONSTANTS } from "data/static";
import BaseSelect from "components/Shared/Base/BaseSelect";
import AddIcon from '@mui/icons-material/Add';
import { DeleteOutline } from "@mui/icons-material";

const INITIAL_VALUE = {
    documents: [{
        title: "",
        attachments: [],
        isActive: "",
        vendorDocumentType: "",
        expiryDate: "",
        hasExpiry: false,
    }],
};

const VendorDocumentForm = ({
    isBusy = false,
    editDocumentData,
    onSubmit = () => { },
    onCancel = () => { },
}) => {
    const location = useLocation();
    const [showExpiry, setShowExpiry] = useState([false]);

    const isVendorProfile = location.pathname.includes('vendor-profile');

    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
    } = useForm({
        defaultValues: INITIAL_VALUE,
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "documents",
    });

    const hasExpiry = watch("documents").map(doc => doc.hasExpiry);

    const { data: vendorDocumentType = [], isLoading: loadingVendorDocumentType } =
        useListListQuery({
            type: ENTITY_LIST_CONSTANTS.VENDOR_DOCUMENT_TYPES,
            status: "Active",
        });

    const _mappedVendorDocumentType = React.useMemo(
        () =>
            Array.isArray(vendorDocumentType)
                ? vendorDocumentType.map((item) => ({
                    label: item?.name,
                    value: item?._id,
                }))
                : [],
        [vendorDocumentType]
    );

    const document = editDocumentData;

    const { handleUploadFiles, uploadingFiles } = useDOUploadFile();
    const { viewEntity } = useAuth();

    React.useEffect(() => {
        if (!document) return;

        const hasExpiryValue = !!document?.expiryDate;

        reset({
            documents: [{
                title: document?.title,
                isActive: document?.isActive,
                expiryDate: document?.expiryDate ? document.expiryDate.split('T')[0] : "",
                vendorDocumentType: document?.vendorDocumentType?._id || null,
                attachments: document?.attachments || [],
                hasExpiry: hasExpiryValue,
            }],
        });

        setShowExpiry([hasExpiryValue]);
    }, [reset, document]);

    const handleSubmitOrderDocument = async (data) => {
        const payloads = data.documents.map(doc => ({
            ...doc,
            expiryDate: doc.hasExpiry ? doc.expiryDate : null,
            vendorDocumentType: doc.vendorDocumentType || null,
        }));

        for (let payload of payloads) {
            const [existingAttachments, newAttachments] = payload?.attachments?.reduce(
                ([pass, fail], el) =>
                    el?.publicId || el?.url ? [[...pass, el], fail] : [pass, [...fail, el]],
                [[], []]
            );

            if (newAttachments?.length > 0) {
                try {
                    const { uploadedFiles } = await handleUploadFiles(newAttachments, {
                        folderName: "Document",
                        entity: viewEntity?.entity?._id,
                    });
                    payload.attachments = [...existingAttachments, ...uploadedFiles];
                } catch (error) {
                    return;
                }
            } else {
                payload.attachments = existingAttachments;
            }
        }

        const requestPayload = {
            documents: payloads,
            source: isVendorProfile ? "VendorProfile" : "OtherSource",
            sourceRef: viewEntity?.entity?._id || "defaultSourceRef",
        };

        onSubmit(requestPayload);
    };

    const addNewDocument = () => {
        append({
            title: "",
            attachments: [],
            isActive: "",
            vendorDocumentType: "",
            expiryDate: "",
            hasExpiry: false,
        });
        setShowExpiry([...showExpiry, false]);
    };

    return (
        <LoadingWrapper loading={isBusy || uploadingFiles}>
            <InnerShadowBox sx={{ padding: 2, backgroundColor: '#f9fafb' }}>
                {fields.map((field, index) => (
                    <Grid container spacing={4} key={field.id} alignItems="center" sx={{ mb: 2.5 }}>
                        <Grid item xs={12} sm={2}>
                            <Controller
                                name={`documents.${index}.title`}
                                control={control}
                                rules={{ required: "Title is required" }}
                                render={({ field }) => (
                                    <BaseTextField
                                        fullWidth
                                        label="Title"
                                        type="text"
                                        size="small"
                                        sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', borderRadius: '8px' } }}
                                        {...field}
                                        onChange={(e) => setValue(`documents.${index}.title`, e.target.value)}
                                        error={!!errors.documents?.[index]?.title}
                                        helperText={errors.documents?.[index]?.title?.message}
                                    />
                                )}
                            />
                        </Grid>

                        {isVendorProfile && (
                            <Grid item xs={12} sm={1.5}>
                                <Controller
                                    name={`documents.${index}.vendorDocumentType`}
                                    control={control}
                                    render={({ field }) => (
                                        <BaseSelect
                                            {...field}
                                            fullWidth
                                            size="small"
                                            label="Document Type"
                                            options={_mappedVendorDocumentType}
                                            getOptionLabel={(opt) => opt.label || ""}
                                            getOptionValue={(opt) => opt.value || ""}
                                            error={!!errors.documents?.[index]?.vendorDocumentType}
                                            helperText={errors.documents?.[index]?.vendorDocumentType?.message}
                                            loading={loadingVendorDocumentType}
                                            loadingText="Loading Document Types..."
                                            sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', borderRadius: '8px' } }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}


                        <Grid item xs={12} sm={isVendorProfile ? 3.5 : 5}>
                            <Controller
                                name={`documents.${index}.attachments`}
                                control={control}
                                render={({ field }) => (
                                    <FileDropzone
                                        files={field.value || []}
                                        onFileChange={(files) => setValue(`documents.${index}.attachments`, files)}
                                        multiple={true}
                                        maxFiles={5}
                                        sx={{ borderRadius: '8px', borderColor: '#d1d5db' }}
                                    />
                                )}
                            />
                        </Grid>


                        {isVendorProfile && (
                            <Grid item xs={12} sm={2} sx={{ pr: 0, mr: -1.25 }}>
                                <Controller
                                    name={`documents.${index}.hasExpiry`}
                                    control={control}
                                    render={({ field }) => (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={field.value || false}
                                                    {...field}
                                                    onChange={(e) => {
                                                        field.onChange(e);
                                                        const newShowExpiry = [...showExpiry];
                                                        newShowExpiry[index] = e.target.checked;
                                                        setShowExpiry(newShowExpiry);
                                                    }}
                                                    size="small"
                                                    sx={{ '& .MuiSwitch-switchBase': { color: '#6b7280' } }}
                                                />
                                            }
                                            label="Has Expiry?"
                                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem', color: '#374151' } }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}



                        {(showExpiry[index] || (isVendorProfile && hasExpiry[index])) && (
                            <Grid item xs={12} sm={2} sx={{ pl: 0, ml: -14.25 }}>
                                <Controller
                                    name={`documents.${index}.expiryDate`}
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            fullWidth
                                            label="Expiry Date"
                                            type="date"
                                            {...field}
                                            InputLabelProps={{ shrink: true }}
                                            error={!!errors.documents?.[index]?.expiryDate}
                                            helperText={errors.documents?.[index]?.expiryDate?.message}
                                            disabled={!hasExpiry[index]}
                                            sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', borderRadius: '8px' } }}
                                        />
                                    )}
                                />
                            </Grid>
                        )}




                        {fields.length > 1 && (
                            <Grid item xs={12} sm={1}>
                                <IconButton onClick={() => remove(index)} sx={{ color: '#ef4444' }}>
                                    <DeleteOutline fontSize="small" />
                                </IconButton>
                            </Grid>
                        )}
                    </Grid>
                ))}

                <Box display="flex" justifyContent="flex-start" mt={2}>
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon fontSize="small" />}
                        onClick={addNewDocument}
                        disabled={isBusy}
                        sx={{
                            fontSize: '0.85rem',
                            borderRadius: '8px',
                            borderColor: '#d1d5db',
                            color: '#374151',
                            '&:hover': { borderColor: '#9ca3af', backgroundColor: '#f3f4f6' },
                        }}
                    >
                        Add Document
                    </Button>
                </Box>
            </InnerShadowBox>

            <Divider sx={{ my: 2, borderColor: '#e5e7eb' }} />

            <Box
                display="flex"
                justifyContent="flex-end"
                gap={1.5}
                paddingX={2}
                paddingY={2}
            >
                <Button
                    disabled={isBusy}
                    onClick={onCancel}
                    sx={{
                        fontSize: '0.85rem',
                        borderRadius: '8px',
                        color: '#374151',
                        '&:hover': { backgroundColor: '#f3f4f6' },
                    }}
                >
                    Cancel
                </Button>

                <Button
                    variant="outlined"
                    disabled={isBusy}
                    onClick={handleSubmit(handleSubmitOrderDocument)}
                    sx={{
                        fontSize: '0.85rem',
                        borderRadius: '8px',
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                        '&:hover': { backgroundColor: '#eff6ff', borderColor: '#2563eb' },
                    }}
                >
                    {document ? "Update" : "Add"}
                </Button>
            </Box>
        </LoadingWrapper>
    );
};

export default VendorDocumentForm;